import { zodResolver } from "@hookform/resolvers/zod";
import { AxiosError, AxiosResponse } from "axios";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import ClientDetailsStep from "./ClientDetailsStep";
import ClientSettingsStep from "./ClientSettingsStep";
import ClientTypeStep from "./ClientTypeStep";

import { useClientSchema } from "@/components/forms/schemas/clientSchema";
import { Button } from "@/components/ui/v2/button";
import { Form } from "@/components/ui/v2/form";
import { Stepper } from "@/components/ui/v2/stepper";
import { AddClientContextType } from "@/lib/contexts/Client/AddClientContext";
import { handleStepFormErrorNavigation } from "@/lib/helpers/stepFormErrorNavigation";
import { usePrompt } from "@/lib/hooks/usePrompt";
import { useStepper } from "@/lib/hooks/useStepper";
import { ClientErrorObject, ClientTypeEnum, PaymentTypeEnum } from "@/types/client/ClientType";
import { AddClientRequest } from "@/types/request/AddClientRequest";
import {
  AddClientErrorResponse,
  AddClientResponse,
} from "@/types/response/AddClientResponse";

// Text fields trimmed before submit (previously handled by register setValueAs)
const TRIMMED_FIELDS: (keyof AddClientRequest)[] = [
  "name",
  "firstName",
  "infix",
  "lastName",
  "emailAddress",
  "jobTitle",
  "feedUrl",
];

export type ClientFormProps = {
  onCancel?: () => void;
  onSubmitSuccess?: (clientId: string) => void;
  confirm?: () => void;
  addClient?: AddClientContextType;
};

const ClientForm: React.FC<ClientFormProps> = ({
  onCancel,
  onSubmitSuccess,
  confirm,
  addClient,
}) => {
  const { t } = useTranslation();

  const steps = [
    { title: t("admin.client.addClient.clientInfo.title") },
    { title: t("admin.client.addClient.clientSettings.title") },
    { title: t("admin.client.addClient.clientType.title") },
  ];

  const addNewClient = addClient?.addClient;
  const isAddClientLoading = addClient?.isPending || false;

  const {
    clientInfo,
    clientLogo,
    clientInfoStep,
    clientLogoStep,
    clientTypeStep,
    atsValidationSchema,
    feedValidationSchema,
  } = useClientSchema();

  const {
    currentStep,
    goToNextStep,
    goToPreviousStep,
    isFirstStep,
    isLastStep,
    goToStep,
  } = useStepper({
    steps,
    onComplete: () => {
      handleAddSubmit();
    },
  });

  const defaultValues: AddClientRequest = {
    clientType: ClientTypeEnum.ATS,
    emailAddress: "",
    feedFormat: "DefaultRecruitNow",
    feedUrl: "",
    firstName: "",
    paymentType: PaymentTypeEnum.SUBSCRIPTION,
    imagePath: "",
    infix: "",
    isSelfService: false,
    jobTitle: "",
    lastName: "",
    name: "",
    phoneNumber: "",
    status: true,
    marginIncluded: false,
  };

  const stepSchema =
    [clientInfoStep, clientLogoStep, clientTypeStep][currentStep] ||
    clientTypeStep;

  const resolver = zodResolver(stepSchema);

  type ClientFormStepValues =
    | z.output<typeof clientInfoStep>
    | z.output<typeof clientLogoStep>
    | z.output<typeof clientTypeStep>;

  const methods = useForm<AddClientRequest, unknown, ClientFormStepValues>({
    resolver,
    defaultValues,
  });

  const {
    formState: { isDirty },
    getValues,
    setError,
    clearErrors,
  } = methods;

  const { markSaved, confirmDialog } = usePrompt(
    {
      title: t("admin.userManagement.addUser.leavePrompt.title"),
      description: t("admin.userManagement.addUser.leavePrompt.description"),
    },
    isDirty
  );

  const getErrorMessage = (field: string, errorMessage: string): string => {
    const fieldName = t(`common.${field}`);
    return t(`errorCodes.${errorMessage}`, { fieldName: t(`${fieldName}`) });
  };

  const handleAddSubmit = () => {
    const formData = { ...getValues() };

    TRIMMED_FIELDS.forEach((key) => {
      const value = formData[key];
      if (typeof value === "string") {
        (formData[key] as string) = value.trim();
      }
    });

    if (formData.clientType === ClientTypeEnum.ATS) {
      formData.feedFormat = "";
      formData.feedUrl = "";
      // ATS credentials are no longer sent - users configure via integrations
    }

    addNewClient?.mutate(formData, {
      onSuccess: (response: AxiosResponse<AddClientResponse>) => {
        markSaved();
        onSubmitSuccess?.(response.data?.id2);
      },
      onError: (error: AxiosError<AddClientErrorResponse>) => {
        handleSubmitError(error);
      },
    });
  };

  const handleSubmitError = (error: AxiosError<AddClientErrorResponse>) => {
    const errors = error.response?.data;
    if (errors && Array.isArray(errors)) {
      errors.forEach(
        ({ propertyName: field, errorMessage }: ClientErrorObject) => {
          const message = getErrorMessage(field, errorMessage);
          setError(field as keyof AddClientRequest, {
            type: "manual",
            message,
          });
        }
      );

      handleStepFormErrorNavigation({
        stepSchemas: {
          0: clientInfo,
          1: clientLogo,
          2: [
            ...new Set([
              ...Object.keys(atsValidationSchema.shape),
              ...Object.keys(feedValidationSchema.shape),
            ]),
          ],
        },
        formMethods: methods,
        goToStep,
      });
    }
  };

  const handlePreviousStep = () => {
    if (isFirstStep) {
      handleCancelButton();
    } else {
      goToPreviousStep();
      clearErrors();
    }
  };

  const handleCancelButton = () => {
    if (isDirty && confirm) {
      confirm();
    } else if (onCancel) {
      onCancel();
    }
  };

  const renderCurrentStep = () => {
    if (currentStep === 0) {
      return <ClientDetailsStep />;
    }
    if (currentStep === 1) {
      return <ClientSettingsStep />;
    }
    return <ClientTypeStep />;
  };

  return (
    <Form {...methods}>
      <form className="w-full max-w-2xl">
        <Stepper steps={steps} currentStep={currentStep} />
        <div className="space-y-6 mt-6">{renderCurrentStep()}</div>

        <div className="flex items-center justify-start gap-2 mt-8">
          <Button type="button" variant="outline" onClick={handlePreviousStep}>
            {isFirstStep ? (
              <X className="size-4" />
            ) : (
              <ArrowLeft className="size-4" />
            )}
            {isFirstStep ? t("common.cancel") : t("common.back")}
          </Button>
          <Button
            type="button"
            onClick={methods.handleSubmit(goToNextStep)}
            loading={isAddClientLoading}
          >
            {isLastStep
              ? t("admin.client.addClient.saveClient", "Save client")
              : t("common.nextStep", "Next step")}
            <ArrowRight className="size-4" />
          </Button>
        </div>
      </form>
      {confirmDialog}
    </Form>
  );
};

export default ClientForm;
